import { cn } from "@u/cn";
import { ChevronDown, Eye } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Mobile-only (`lg:hidden`) collapsible disclosure for an editor's device/card
 * preview. Desktop keeps its own sticky column; below `lg` the preview would be
 * either hidden (no access) or stacked inline, so it lives behind this toggle so
 * color/logo/content edits aren't blind on a phone. Collapsed by default.
 *
 * Two modes:
 *  - default (duplicate): renders `lg:hidden`, a mobile-only copy of a preview
 *    that has its own sticky desktop column. Cheap-to-duplicate phone/card
 *    mocks. Pass `unmount` to drop children when collapsed.
 *  - `inline`: wraps the ONE real preview element in place — always shown at
 *    `lg+`, collapsible below `lg` via a mobile-only toggle. Use for canvas /
 *    single-WebGL-context previews that can't be duplicated or unmounted
 *    (share studios, R3F viewer preview). Children always stay mounted.
 */
export default function MobilePreview({
  children,
  label,
  defaultOpen = false,
  unmount = false,
  inline = false,
  className,
}: {
  children: ReactNode;
  /** Toggle label. Defaults to `t("preview")`. */
  label?: string;
  defaultOpen?: boolean;
  /** Drop children from the tree when collapsed (phone mocks). Ignored when `inline`. */
  unmount?: boolean;
  /** Keep the single child in place: shown at `lg+`, collapsible below. */
  inline?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const toggle = (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
      className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2.5 text-xs font-mono uppercase tracking-widest text-muted-foreground"
    >
      <span className="flex items-center gap-2">
        <Eye size={14} />
        {label ?? t("preview")}
      </span>
      <ChevronDown
        size={14}
        className={cn("transition-transform", open && "rotate-180")}
      />
    </button>
  );

  // Inline: one child, always visible at lg, toggle only below lg.
  if (inline) {
    return (
      <div className={className}>
        <div className="lg:hidden">{toggle}</div>
        <div className={cn("mt-3 lg:mt-0", open ? "" : "hidden lg:block")}>
          {children}
        </div>
      </div>
    );
  }

  // Duplicate: mobile-only copy (desktop keeps its own sticky column).
  return (
    <div className={cn("lg:hidden", className)}>
      {toggle}
      {unmount ? (
        open && (
          <div className="mt-3 flex flex-col items-center gap-3 overflow-x-auto pb-2">
            {children}
          </div>
        )
      ) : (
        <div
          className={cn(
            "mt-3 flex-col items-center gap-3 overflow-x-auto pb-2",
            open ? "flex" : "hidden",
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
